import React from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import LicenseGate from '../components/ui/LicenseGate';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import EmptyState from '../components/ui/EmptyState';
import { 
  BrandKPIsSection, 
  BrandRevenueTrendChart, 
  BrandCustomerTrendChart,
  BrandSatisfactionAnalysisChart,
  BrandProductDistributionChart,
  BrandCustomerRetentionChart,
  BrandOverviewBranchComparisonTable
} from '../components/brand';

/**
 * Props for the BrandOverviewDashboard component
 */
interface BrandOverviewDashboardProps {
  /** Optional brand ID to override context value */
  brandId?: number;
  /** Optional time range to override context value (e.g., 'last6Months', 'last12Months') */
  timeRange?: string;
}

/**
 * Brand Overview Dashboard Component
 * 
 * Displays aggregated key performance indicators (KPIs) and trends for a multi-branch brand.
 * Shows total revenue, customer count, satisfaction scores, and other metrics across all branches.
 * 
 * Features:
 * - Aggregated KPIs with currency normalization
 * - Revenue and customer trend charts
 * - Loading and error states
 * - Empty state handling
 * - Accessibility support (ARIA labels, keyboard navigation)
 * - Responsive design (mobile, tablet, desktop)
 * 
 * @component
 * @example
 * ```tsx
 * <BrandOverviewDashboard brandId={123} timeRange="last6Months" />
 * ```
 */

const BrandOverviewDashboard: React.FC<BrandOverviewDashboardProps> = ({
  brandId: propBrandId,
  timeRange: propTimeRange
}) => {
  // Get brand context
  const { selectedBranch } = useBranch();
  const { timeFrame } = useTimeFrame();
  
  // Use prop values or fall back to context
  const brandId = propBrandId || selectedBranch?.brandId || null;
  const timeRange = propTimeRange || timeFrame || 'last6Months';

  if (!brandId) {
    return (
      <LicenseGate licenseType="club" dashboardName="نمای کلی برند">
        <div className="container-dashboard" dir="rtl">
          <h1 className="title-dashboard">نمای کلی برند</h1>
          <EmptyState
            message="ابتدا یک برند را از نوار بالای صفحه انتخاب کنید"
            icon="🏷️"
          />
        </div>
      </LicenseGate>
    );
  }
  
  return (
    <LicenseGate licenseType="club" dashboardName="نمای کلی برند">
      <div className="container-dashboard" dir="rtl">
        {/* Skip to main content link for keyboard users */}
        <a
          href="#brand-overview-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:right-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
        >
          پرش به محتوای اصلی
        </a>
        
        <main id="brand-overview-content" className="space-y-6 md:space-y-8 lg:space-y-10 animate-enter-up" role="main" aria-label="داشبورد نمای کلی برند">
          {/* Dashboard title using standard class - responsive sizing */}
          <header>
            <h1 className="title-dashboard text-2xl md:text-3xl" id="dashboard-title">نمای کلی برند</h1>
          </header>
          
          {/* TODO: Add TimePeriodSelector component */}
          
          {/* KPI Section with stagger animation - responsive spacing */}
          <section aria-labelledby="kpis-heading" className="animate-scale-in stagger-2">
            <h2 id="kpis-heading" className="sr-only">شاخص‌های کلیدی عملکرد</h2>
            <DashboardSectionAnchor sectionId="brand-kpis">
              <BrandKPIsSection brandId={brandId!} timeRange={timeRange} />
            </DashboardSectionAnchor>
          </section>
          
          {/* Revenue and Customer Trends - responsive grid
          <section aria-labelledby="trends-heading" className="animate-scale-in stagger-3">
            <h2 id="trends-heading" className="sr-only">نمودارهای روند فروش و مشتریان</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <BrandRevenueTrendChart
                data={overviewData?.revenueTrend}
                loading={overviewLoading}
                currency={overviewData?.kpis?.currency}
                brandId={brandId!}
                timeRange={timeRange}
              />
              
              <BrandCustomerTrendChart
                data={overviewData?.customerTrend}
                loading={overviewLoading}
                brandId={brandId!}
                timeRange={timeRange}
              />
            </div>
          </section>
          
          New Analysis Charts - responsive grid
          <section aria-labelledby="analysis-heading" className="animate-scale-in stagger-4">
            

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              Satisfaction Analysis
              <BrandSatisfactionAnalysisChart
                brandId={brandId!}
                timeRange={timeRange}
              />
              
              Product Distribution
              <BrandProductDistributionChart
                brandId={brandId!}
                timeRange={timeRange}
              />
            </div>
            
            Customer Retention - Full width
            <div className="mt-4 md:mt-6">
              <BrandCustomerRetentionChart
                brandId={brandId!}
                timeRange={timeRange}
              />
            </div>
          </section> */}

          {/* New Analysis Charts - responsive grid */}
          <section aria-labelledby="analysis-heading" className="animate-scale-in stagger-4">
            {/* <h2 id="analysis-heading" className="title-section mb-4 text-xl md:text-2xl">تحلیل‌های تکمیلی</h2> */}

            <div className="mb-4">
              <DashboardSectionAnchor sectionId="brand-branch-comparison">
                <BrandOverviewBranchComparisonTable
                  brandId={brandId}
                  timeRange={timeRange}
                />
              </DashboardSectionAnchor>
            </div>
          </section>
        </main>
      </div>
    </LicenseGate>
  );
};

export default BrandOverviewDashboard;
